// Sugar Alarm — cycle 454: the Stop control on a high alarm.
//
// Operator decision, cycle 454: High and Urgent High are Stop-only, and their
// Stop is a small pill, right-aligned in the banner. The DRAWN pill is
// compact; the BUTTON is the tap target and is never smaller than 44 × 44 px
// (the pill sits centred inside a transparent box). The label is the 13 px
// type floor in the banner's dark ink on a white pill (8.6:1 amber / 9.4:1
// red — the Snooze pill's pair, `alarmPrimaryInk`).
//
// ⚠ THE HIT BOX IS THE BUTTON'S OWN BOX — no negative margins, no pseudo-
// element reaching outside it — so it can never overlap the banner's text or
// run past the banner's edge: flex layout places the box, and the box is the
// target. Used by BOTH high banners (Now's `HighAlarmBanner` and the off-Now
// `CompactAlarmControl`) so the two cannot drift.
//
// ⚠ CYCLE 460 — ONE PILL, TWO STYLES. THIS IS THE SHARED ALARM-PILL CONTRACT;
// DO NOT FORK IT. The Low banner's Snooze and Stop are now pills with this
// exact geometry (operator decision): `AlarmPill` draws either style —
//   · `filled`   — the white pill above, unchanged;
//   · `outlined` — transparent, a white border, a white label.
// Both share every geometry value (`.hit` and `.pill` in the stylesheet are
// byte-identical to cycle 454) and the ≥ 44 × 44 px button target.
// `AlarmStopPill` is `AlarmPill` with the cycle-454 label and marker; the
// high banners call it exactly as before and render byte-identical markup
// (`cycle460LowPills.test.ts` compares them to a fixture captured from the
// cycle-459 tree). `AlarmPillPair` lays two pills out at one shared width.
// One place reads short words: the Low banner on a portrait phone, where a
// single 52 px row cannot carry "LOW" and two full-size "… sounds" pills —
// there the Low pills read "Snooze" / "Stop" (`shortLabel`, operator
// decisions: cycle 460 for the bedside strip, cycle 461 for every portrait
// phone Low banner, so it is the High banner's 52 px), same geometry and style.
//
// ⚠ CYCLE 461 — THE HIGH BANNERS' STOP READS "Stop sounds" TOO (operator
// decision, 2026-10-09). `AlarmStopPill`'s label is `STOP_SOUNDS_LABEL`, the
// same string as the Low banner's Stop; everything else about the high pill
// (markup, classes, geometry, ink, marker, placement) is cycle 454's.
//
// ⚠ THE OUTLINED LABEL IS 1.76:1 ON THE AMBER — AN OPERATOR EXEMPTION, NOT AN
// OVERSIGHT (cycle 460, 2026-10-07). White on `--alarm-warn-fill` (#f5b942)
// is below the cycle-452.2 4.5:1 floor on every theme block except
// web-light (5.0:1). Asked with the numbers in hand, the operator chose the
// briefed look and accepted the ratio. It is the ONE exempt alarm-control
// text: the outlined label carries `data-contrast-exempt="low-outlined-pill"`,
// the type-floor sweep records its ratio without failing it, and a test pins
// that no other value and no other element may carry the marker.

import type { ReactNode } from "react";

import type { AlarmTone } from "./alarmBannerStyle";
import { alarmPrimaryInk } from "./alarmBannerStyle";
import styles from "./AlarmStopPill.module.css";

/** The two drawn styles of the shared alarm pill (cycle 460). */
export type AlarmPillAppearance = "filled" | "outlined";

/** The alarm pills' labels (operator decisions): they quiet the AUDIO, they
 *  do not dismiss the alarm. Visible text and `aria-label` alike. Cycle 460
 *  gave the Low banner "Snooze sounds" / "Stop sounds"; cycle 461 gave the
 *  high banners' Stop the same "Stop sounds". */
export const STOP_SOUNDS_LABEL = "Stop sounds";
export const LOW_SNOOZE_LABEL = "Snooze sounds";
export const LOW_STOP_LABEL = STOP_SOUNDS_LABEL;

/** The marker the type-floor sweep reads; the only permitted value. */
export const OUTLINED_PILL_CONTRAST_EXEMPTION = "low-outlined-pill";

/** The short words the Low pills read on a portrait phone (cycle 460 for
 *  the bedside strip, cycle 461 for every phone Low banner — see
 *  `shortLabel`). */
export const LOW_SNOOZE_SHORT_LABEL = "Snooze";
export const LOW_STOP_SHORT_LABEL = "Stop";

export interface AlarmPillProps {
  tone: AlarmTone;
  label: string;
  onPress: () => void;
  appearance?: AlarmPillAppearance;
  testId?: string;
  /** Data attributes on the button, in render order (the high pill's
   *  `data-alarm-stop-pill` marker rides here). */
  marker?: Record<`data-${string}`, string>;
  /** Cycles 460 / 461 (operator decisions): on a portrait phone "LOW" + two
   *  full-size "… sounds" pills need about 395 px on one row — more than a
   *  390 / 375 / 360 phone has — and the Low banner must be the High
   *  banner's 52 px. There the Low pill reads `shortLabel` — same geometry,
   *  same style.
   *  Both words are JSX text; CSS shows one (`@media (max-width: 600px)`,
   *  the portrait-phone breakpoint) and the hidden one is `display: none`,
   *  so the button's accessible name is always the visible word. */
  shortLabel?: string;
}

export function AlarmPill({ tone, label, onPress, appearance = "filled", testId, marker, shortLabel }: AlarmPillProps) {
  const outlined = appearance === "outlined";
  const pillClass = outlined ? `${styles.pill} ${styles.outlined}` : styles.pill;
  const ink = outlined ? undefined : { color: alarmPrimaryInk(tone) };
  const exempt = outlined ? OUTLINED_PILL_CONTRAST_EXEMPTION : undefined;
  if (shortLabel != null) {
    return (
      <button
        type="button"
        className={`${styles.hit} ${styles.shortable}`}
        data-testid={testId}
        {...marker}
        onClick={onPress}
      >
        <span className={pillClass} style={ink} data-contrast-exempt={exempt}>
          <span className={styles.fullLabel} data-label="full">{label}</span>
          <span className={styles.shortLabel} data-label="short">{shortLabel}</span>
        </span>
      </button>
    );
  }
  return (
    <button
      type="button"
      className={styles.hit}
      data-testid={testId}
      {...marker}
      aria-label={label}
      onClick={onPress}
    >
      <span
        className={pillClass}
        style={ink}
        data-contrast-exempt={exempt}
        aria-hidden
      >
        {label}
      </span>
    </button>
  );
}

export interface AlarmStopPillProps {
  tone: AlarmTone;
  onStop: () => void;
  testId?: string;
}

const STOP_PILL_MARKER = { "data-alarm-stop-pill": "" } as const;

export function AlarmStopPill({ tone, onStop, testId }: AlarmStopPillProps) {
  return <AlarmPill tone={tone} label={STOP_SOUNDS_LABEL} onPress={onStop} testId={testId} marker={STOP_PILL_MARKER} />;
}

/** Two (or one) pills, right-aligned as a unit, every pill the width of the
 *  widest label (equal grid tracks) — never stretched to fill the row. */
export function AlarmPillPair({ children }: { children: ReactNode }) {
  return <span className={styles.pair}>{children}</span>;
}
